import { format } from 'date-fns';
import delve from 'dlv';

import { ICartEntry, IChannelExclusions, IOffer, ISection, MenuObject } from '@rbi-ctg/menu';
import { IEvaluationFeedback, IRedemptionEligibility } from '@rbi-ctg/offers';
import { MenuObjectTypes } from 'enums/menu';
import { CartOfferState, IOfferFeedbackEntryFragment } from 'generated/rbi-graphql';
import { LoyaltyOffer } from 'state/loyalty/types';
import { ILockedOffer } from 'state/offers/hooks/use-locked-offers';

import { isNative, isWeb, platform } from '../environment';

import { USE_TOKEN_ID_AS_OFFER_ID } from './constants';
import { OfferPlatform } from './rule-sets/types';

export enum OfferActivationStatus {
  CAN_ACTIVATE = 'CAN_ACTIVATE',
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
  ACTIVATED = 'ACTIVATED',
}

export const activationStatusFromOfferState = (offerState: CartOfferState | null) => {
  switch (offerState) {
    case CartOfferState.ACTIVE:
      return OfferActivationStatus.ACTIVATED;
    default:
      return OfferActivationStatus.CAN_ACTIVATE;
  }
};

/**
 * Resolves to highest level menuObject of type
 * 'Combo' or 'Item' for a menuObject tree.
 */
export const flattenMenuObject = (menuObject: MenuObject): string[] => {
  // Root case, we've met first Item/Combo in menuObject tree.
  if (menuObject._type === 'combo' || menuObject._type === 'item') {
    return [menuObject._id];
  }

  // This is a little complicated, but recursively calls the
  // children and flattens returned arrays from callstack.
  if (menuObject.options) {
    // @ts-ignore this cast is probably not strictly correct but I could not get
    // typescript to resolve the correct call signature without refining to a more specific type
    return (menuObject as ISection).options.reduce<string[]>(
      (accum: string[], childMenuObject: MenuObject): string[] => {
        const childObject: MenuObject = (childMenuObject as any).option || childMenuObject;
        const flattenedChild: string[] = flattenMenuObject(childObject);
        return accum.concat(flattenedChild);
      },
      []
    );
  }

  return [];
};

/**
 * Reduces requirements and cartEntries to necessary id sets and
 * returns true if they share an id.
 * @deprecated
 */
export const verifyPurchase = (purchaseRequirement: MenuObject, cartEntries: ICartEntry[]) => {
  const requiredIds: string[] = flattenMenuObject(purchaseRequirement);

  return cartEntries.some(cartEntry => requiredIds.includes(cartEntry._id));
};

/**
 * @deprecated
 */
export const verifyPlatform = (couponPlatform: OfferPlatform): boolean => {
  return platform().toLowerCase() === couponPlatform.toLowerCase();
};

/**
 * @deprecated
 */
export const channelExclusion = (isDelivery: boolean) => (offer: IOffer) => {
  const exclusions: IChannelExclusions = delve(offer, 'channelExclusions') || {};

  if (exclusions.web) {
    return !isWeb;
  }
  if (exclusions.kiosk) {
    return false;
  }
  if (exclusions.mobile) {
    return !isNative;
  }
  if (exclusions.delivery) {
    return !isDelivery;
  }
  return true;
};

export const getOfferFeedbackErrors = (offer: IRedemptionEligibility) => {
  return delve(offer, 'evaluationFeedback', []).map(
    (evaluationFeedback: IEvaluationFeedback) =>
      `#${evaluationFeedback.code} - ${evaluationFeedback.message}`
  );
};

export const reduceOffersFeedback = (
  offersFeedback: ReadonlyArray<IOfferFeedbackEntryFragment>
): Record<string, IOfferFeedbackEntryFragment> =>
  offersFeedback.reduce(
    (acc, entry) => ({
      ...acc,
      [entry.tokenId || entry.couponId]: entry,
    }),
    {}
  );

export const offersFeedbacksById = (
  offersFeedback: ReadonlyArray<IOfferFeedbackEntryFragment>
): Record<string, IOfferFeedbackEntryFragment[] | undefined> =>
  offersFeedback.reduce((acc, entry) => {
    const existingFeedbacks = acc[entry._id] || [];
    return { ...acc, [entry._id]: [...existingFeedbacks, entry] };
  }, {});

export function getAnonEndingDate() {
  return new Date().setDate(new Date().getDate() + 1);
}

export const makeUrlForOffer = (offer: IOffer) => {
  if (!offer.option || !offer.vendorConfigs) {
    return null;
  }
  const {
    option: { _id, _type },
  } = offer;

  if (_type === MenuObjectTypes.OFFER_DISCOUNT) {
    return null;
  }

  return `/menu/${_type}-${_id}?isOffer=true`;
};

export const isMoreInfoDisclaimerAvailable = (offer: IOffer | LoyaltyOffer): boolean => {
  const moreInfoText = offer?.moreInfo?.localeRaw?.[0]?.children?.[0]?.text;
  return !!moreInfoText;
};

export const redeemedOnTime = (): string => format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");

/**
 * NOTE: the following functions assist in getting / using offers based on a uniq offer id
 *       the value used for a uniq offer is not always consistent across brands. sometimes
 *       we use the offer _id for uniqueness and sometimes we use the offer tokenId. These
 *       utils give us a consistent way of determining what uniq id to use for an offer.
 * For example, Tim's allows a single
 *  offer to be assigned to a user multiple times. In this case, we
 *  would have a repeated offer _id, but a uniq offer tokenId in the
 *  offer list
 */

interface IGetUniqIdForOfferProps {
  id: string;
  token?: string | null | undefined;
}

/**
 * @name getUniqIdForOffer
 * @description get the uniq id based on a constant and the data available on the offer
 */
export const getUniqIdForOffer = ({ id, token }: IGetUniqIdForOfferProps): string => {
  if (USE_TOKEN_ID_AS_OFFER_ID && token) {
    return token;
  }
  return id;
};

/**
 * @name isCurrentlySelectedOffer
 * @description determine if an offer is already the selected offer
 */
export const isCurrentlySelectedOffer = (
  uniqOfferId: string,
  selectedOffer: IOffer | ILockedOffer
): boolean => selectedOffer.tokenId === uniqOfferId || selectedOffer._id === uniqOfferId;

export const findOfferByUniqId = (
  id: string,
  offers: IOffer[] | ILockedOffer[] = []
): IOffer | ILockedOffer | undefined =>
  offers.find(offer => offer.tokenId === id || offer._id === id);

export const findOfferIndexByUniqId = (
  id: string,
  offers: IOffer[] | ILockedOffer[] = []
): number => offers.findIndex(offer => offer.tokenId === id || offer._id === id);

/**
 * END utils for uniq offer id
 */

// ruleset utils
export * from './rule-sets';

export * from './sort-offers-for-service-mode';
